import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps, Response } from '@ui/src';
import {
  CorporateOverviewGeneral,
  CorporateCustomerEntityForm,
  CofProps,
} from '../../types';
import { corporateCustomersApi } from '../services/corporate';
import { customersComponentApi } from '../../../customers/store/services/customers';
import { CustomerProps } from '../../../common/types';
import { LinkedClient } from '../../../common/linked-clients/types';

export interface CorporateCustomerOverviewState {
  baseInfo?: Partial<CorporateOverviewGeneral>;
  companyOfficial?: CofProps[];
  transactionSigningRule: {
    isLoading: boolean;
    value?: string;
    defaultValue?: string;
    requestInProgress?: boolean;
    rulesList?: SelectItemProps[];
  };
  individualUserList?: CustomerProps[];
}

const initialState: CorporateCustomerOverviewState = {
  transactionSigningRule: {
    isLoading: false,
  },
};

export const CorporateCustomerOverviewSlice = createSlice({
  name: 'corporateCustomerOverview',
  initialState,
  reducers: {
    clearOverviewState: (state) => {
      state.companyOfficial = undefined;
      state.transactionSigningRule = {
        isLoading: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.bindExternalOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: LinkedClient }>) => {
        if (state.baseInfo) {
          state.baseInfo = {
            ...state.baseInfo,
            pspIntegrationOrganization: action.payload.data,
          };
        }
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateUser.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        state.baseInfo = {
          companyNumber: data.companyNumber,
          entityForm: CorporateCustomerEntityForm.LTD,
          dateOfIncorporation: data.dateOfIncorporation,
          countryOfIncorporationId: data.countryOfIncorporationId,
          sicCodes: data.sicCodes,
          merchantId: data?.merchantId,
          // mccCode: data?.mccCode || '-',
          status: data.status,
          correspondenceAddressSameAsRegisteredAddress:
            data.correspondenceAddressSameAsRegisteredAddress,
          businessAddressSameAsRegisteredAddress:
            data.businessAddressSameAsRegisteredAddress,
          correspondentAddress:
            data.correspondenceAddressSameAsRegisteredAddress
              ? `${data.registeredAddress1}${
                  data.registeredAddress2 ? `, ${data.registeredAddress2}` : ''
                }${data.registeredCity ? `, ${data.registeredCity}` : ''}${
                  data.registeredPostalCode
                    ? `, ${data.registeredPostalCode}`
                    : ''
                }`
              : `${data.correspondenceAddress1 || ''}${
                  data.correspondenceAddress2
                    ? `, ${data.correspondenceAddress2}`
                    : ''
                }${
                  data.correspondenceCity ? `, ${data.correspondenceCity}` : ''
                }${
                  data.correspondencePostalCode
                    ? `, ${data.correspondencePostalCode}`
                    : ''
                }`,
          registrationAddress: `${data.registeredAddress1}${
            data.registeredAddress2 ? `, ${data.registeredAddress2}` : ''
          }${data.registeredCity ? `, ${data.registeredCity}` : ''}${
            data.registeredPostalCode ? `, ${data.registeredPostalCode}` : ''
          }`,
          businessAddress: data.businessAddressSameAsRegisteredAddress
            ? `${data.registeredAddress1}${
                data.registeredAddress2 ? `, ${data.registeredAddress2}` : ''
              }${data.registeredCity ? `, ${data.registeredCity}` : ''}${
                data.registeredPostalCode
                  ? `, ${data.registeredPostalCode}`
                  : ''
              }`
            : `${data.businessAddress1 || ''}${
                data.businessAddress2 ? `, ${data.businessAddress2}` : ''
              }${data.businessCity ? `, ${data.businessCity}` : ''}${
                data.businessPostalCode ? `, ${data.businessPostalCode}` : ''
              }`,
          websiteLink: data.websiteLink,
          businessDescription: data.businessDescription,
          correspondenceCountryId: data.correspondenceCountryId,
          correspondenceCity: data.correspondenceCity,
          correspondencePostalCode: data.correspondencePostalCode,
          correspondenceAddress1: data.correspondenceAddress1,
          correspondenceAddress2: data.correspondenceAddress2,
          businessCountryId: data.businessCountryId,
          businessCity: data.businessCity,
          businessPostalCode: data.businessPostalCode,
          businessAddress1: data.businessAddress1,
          businessAddress2: data.businessAddress2,
          registeredCountryId: data.registeredCountryId,
          registeredCity: data.registeredCity,
          registeredPostalCode: data.registeredPostalCode,
          registeredAddress1: data.registeredAddress1,
          registeredAddress2: data.registeredAddress2,
          companyName: data.companyName,
          productIds: data?.products?.edges?.map((item) => item.node.id),
          vatNumber: data?.vatNumber,
          pspIntegrationOrganization: data?.pspIntegrationOrganization,
        };
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.updateCorporateInfo.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        state.baseInfo = {
          ...data,
          entityForm: CorporateCustomerEntityForm.LTD,
          merchantId: data?.merchantId,
          correspondentAddress:
            data.correspondenceAddressSameAsRegisteredAddress
              ? `${data.registeredAddress1}${
                  data.registeredAddress2 ? `, ${data.registeredAddress2}` : ''
                }${data.registeredCity ? `, ${data.registeredCity}` : ''}${
                  data.registeredPostalCode
                    ? `, ${data.registeredPostalCode}`
                    : ''
                }`
              : `${data.correspondenceAddress1}${
                  data.correspondenceAddress2
                    ? `, ${data.correspondenceAddress2}`
                    : ''
                }${
                  data.correspondenceCity ? `, ${data.correspondenceCity}` : ''
                }${
                  data.correspondencePostalCode
                    ? `, ${data.correspondencePostalCode}`
                    : ''
                }`,
          registrationAddress: `${data.registeredAddress1}${
            data.registeredAddress2 ? `, ${data.registeredAddress2}` : ''
          }${data.registeredCity ? `, ${data.registeredCity}` : ''}${
            data.registeredPostalCode ? `, ${data.registeredPostalCode}` : ''
          }`,
          productIds: data?.products?.edges?.map((item) => item.node.id),
          businessAddress: data.businessAddressSameAsRegisteredAddress
            ? `${data.registeredAddress1}${
                data.registeredAddress2 ? `, ${data.registeredAddress2}` : ''
              }${data.registeredCity ? `, ${data.registeredCity}` : ''}${
                data.registeredPostalCode
                  ? `, ${data.registeredPostalCode}`
                  : ''
              }`
            : `${data.businessAddress1}${
                data.businessAddress2 ? `, ${data.businessAddress2}` : ''
              }${data.businessCity ? `, ${data.businessCity}` : ''}${
                data.businessPostalCode ? `, ${data.businessPostalCode}` : ''
              }`,
          amlCtfComplianceProgram: data?.amlCtfComplianceProgram as boolean,
          purposeOfAccount: data?.purposeOfAccount,
          expectedTypeOfClients: data?.expectedTypeOfClients,
          requiredCurrenciesCryptoId: data?.requiredCurrenciesCryptoId,
        };
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateCOF.matchFulfilled,
      (state, action: PayloadAction<Response<CofProps>>) => {
        state.companyOfficial = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getIndividualUsersList.matchFulfilled,
      (state, action: PayloadAction<Response<CustomerProps>>) => {
        state.individualUserList = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.updateCorporateCOF.matchFulfilled,
      (state, action: PayloadAction<{ data: CofProps }>) => {
        const nextCompanyOfficial = state.companyOfficial?.map((item) => {
          let result = { ...item };

          if (action.payload.data.id === result.id) {
            result = {
              ...action.payload.data,
              officialApplication: result.officialApplication,
              user: {
                ...action.payload.data.user,
                organization: result.user.organization,
              },
            };
          }

          return result;
        });

        state.companyOfficial = nextCompanyOfficial;
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.updateCofStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: CofProps }>) => {
        if (state.companyOfficial) {
          const nextCompanyOfficial = state.companyOfficial?.map((item) => {
            let result = { ...item };

            if (action.payload.data.id === result.id) {
              result = {
                ...action.payload.data,
                officialApplication: result.officialApplication,
                user: {
                  ...action.payload.data.user,
                  organization: result.user.organization,
                },
              };
            }

            return result;
          });

          state.companyOfficial = nextCompanyOfficial;
        }
      },
    );
  },
});

export const { clearOverviewState } = CorporateCustomerOverviewSlice.actions;

export default CorporateCustomerOverviewSlice.reducer;
